import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { orientProject } from "../../lib/api";

// Discreet AI assistant field — analyses a project and recommends a pillar
export const AIAssistant = ({ variant = "dark" }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (value.trim().length < 5 || loading) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const data = await orientProject(value.trim());
      setResult(data);
    } catch {
      setError("L'assistant est momentanément indisponible. Écrivez-nous directement.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";

  return (
    <div className="w-full max-w-xl" data-testid="ai-assistant">
      <form onSubmit={submit} className="relative group">
        <motion.div
          className="absolute -inset-[3px] rounded-full bg-gradient-to-r from-gold via-gold-light to-gold blur-[10px]"
          aria-hidden="true"
          animate={{ opacity: [0.55, 0.9, 0.55] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className={`relative flex items-center gap-2.5 rounded-full pl-5 pr-2 py-2.5 ${isDark ? "bg-white/[0.14] backdrop-blur-xl" : "bg-white"} border-2 border-gold shadow-[0_8px_30px_-8px_rgba(212,160,23,0.6)]`}>
          <Sparkles size={20} className="text-gold-light shrink-0" />
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Décrivez votre projet…"
            aria-label="Décrivez votre projet pour une orientation intelligente"
            data-testid="ai-assistant-input"
            className={`flex-1 bg-transparent outline-none text-sm font-body font-medium ${isDark ? "text-white placeholder:text-white/70" : "text-navy placeholder:text-anthracite/60"}`}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="ai-assistant-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-gold to-gold-light px-4 py-2 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Orienter <ArrowRight size={14} /></>}
          </button>
        </div>
      </form>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: 8, height: 0 }}
            className="mt-4"
            data-testid="ai-assistant-result"
          >
            <div className="rounded-2xl border border-gold/30 bg-navy/60 backdrop-blur-md p-5 text-left">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold mb-2">Pôle recommandé</p>
              <p className="font-display text-lg text-white mb-2">{result.pillar_title}</p>
              <p className="text-sm text-white/70 font-body leading-relaxed">{result.reasoning}</p>
              <Link to="/expertises" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:gap-2.5 transition-all" data-testid="ai-assistant-cta">
                Explorer ce pôle <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
        {error && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 text-sm text-gold-light font-body">
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};
